import circle from '@turf/circle';
import { Feature, FeatureCollection, Polygon } from 'geojson';
import { useMemo } from 'react';
import { Source } from 'react-map-gl';

import {
  getOpacityForReportMarker,
  getReportMapVisibilityHours,
} from 'features/mapping/icons/icons';
import { Report, ReportPhoto } from 'features/reports/report.types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { Layer, LayerProps } from 'shared/map-exports';
import { REPORT_MAP_OBFUSCATION } from '../../../../../constants';

export type ReportObfuscationLayerProps = {
  beforeId?: string;
  reports: (Report | ReportPhoto)[];
  sourceId: string;
  isFadable?: boolean;
} & (
  | { geoEventType: GeoEventType; maxAgeHours?: never }
  | { maxAgeHours?: number; geoEventType?: never }
);

type ObfuscationFeatureProperties = {
  opacity: number;
};

const obfuscationFillStyle: LayerProps = {
  type: 'fill',
  minzoom: REPORT_MAP_OBFUSCATION.MIN_ZOOM,
  maxzoom: REPORT_MAP_OBFUSCATION.MAX_ZOOM,
  paint: {
    'fill-color': REPORT_MAP_OBFUSCATION.COLOR,
    'fill-opacity': ['get', 'opacity'],
  },
};

export const ReportObfuscationLayer = (
  props: ReportObfuscationLayerProps,
): JSX.Element | null => {
  const {
    beforeId,
    reports,
    sourceId,
    isFadable = false,
    geoEventType,
    maxAgeHours,
  } = props;

  const obfuscationGeoJson: FeatureCollection<
    Polygon,
    ObfuscationFeatureProperties
  > | null = useMemo(() => {
    if (!reports.length) return null;

    const uniqueLocations = new Set<string>();
    const filterFunc = (r: Report | ReportPhoto): boolean => {
      const hasLatLng = r.lat != null && r.lng != null;
      if (!hasLatLng || uniqueLocations.has(`${r.lat},${r.lng}`)) {
        return false;
      }
      uniqueLocations.add(`${r.lat},${r.lng}`);
      return true;
    };

    const features = reports.filter(filterFunc).map((report) => {
      const feature = circle(
        [report.lng!, report.lat!],
        REPORT_MAP_OBFUSCATION.RADIUS_METERS,
        { units: 'meters' },
      ) as Feature<Polygon, ObfuscationFeatureProperties>;

      const resolvedMaxAgeHours =
        maxAgeHours ??
        getReportMapVisibilityHours(
          'geoEventType' in report ? report.geoEventType : geoEventType,
        );

      const opacity =
        getOpacityForReportMarker(
          report.dateCreated,
          isFadable,
          resolvedMaxAgeHours,
        ) * REPORT_MAP_OBFUSCATION.OPACITY;
      feature.properties = { opacity };

      return feature;
    });

    return { type: 'FeatureCollection', features };
  }, [reports, isFadable, geoEventType, maxAgeHours]);

  return obfuscationGeoJson ? (
    <Source id={sourceId} type="geojson" data={obfuscationGeoJson}>
      <Layer
        id={`${sourceId}-fill`}
        beforeId={beforeId}
        {...obfuscationFillStyle}
      />
    </Source>
  ) : null;
};
